import { useLocation, useNavigate } from "react-router-dom";
import useExitPrompt from "../../../core/utils/UseExitPrompt";
import { useEffect, useState } from "react";
import { HorizontalOnlyContainer } from "../../component/HorizontalOnlyContainer";
import { GameViewModel } from "./GameViewModel";
import { Category } from "../../../domain/model/Category";
import { GameData, TeamTurns } from "../../../domain/model/Game";
import { Difficulty } from "../../../domain/model/Question";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid2,
  IconButton,
  Stack,
} from "@mui/material";
import { CustomImageView } from "../../component/CustomImageView";
import {
  COLOR_BLACK,
  COLOR_GOLD,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_WHITE,
} from "../../../data/colors/Colors";
import { CustomTextView } from "../../component/CustomTextView";
import { useTranslation } from "react-i18next";
import useWindowDimensions from "../../../core/utils/UseWindowDimensions";
import { LifelinesView } from "../../component/LifelinesView";
import { ExitToAppRounded } from "@mui/icons-material";
import { getDifficultyInfo } from "../../../core/utils/Utils";
import { QuestionView } from "./question/QuestionView";
import { CustomPopoverView } from "../../component/CustomPopoverView";
import {
  provideGetAnswerByQuestionIdUseCase,
  provideGetRandomQuestionUseCase,
  providePlayerAddToGamesUseCase,
  providePlayerAddToHistoryUseCase,
  providePlayerAddToUnfinishedGamesUseCase,
  provideQuestionGiveAnswerUseCase,
  provideUpdateGameUseCase,
} from "../../di/UseCasesModule";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";
import { CustomAlertDialog } from "../../component/CustomAlertDialog";

export function GameView() {
  const location = useLocation();
  const viewModel = GameViewModel(
    provideGetRandomQuestionUseCase(),
    provideGetAnswerByQuestionIdUseCase(),
    providePlayerAddToHistoryUseCase(),
    provideQuestionGiveAnswerUseCase(),
    provideUpdateGameUseCase(),
    providePlayerAddToUnfinishedGamesUseCase()
  );
  useExitPrompt(true);

  const { height } = useWindowDimensions();
  const isMobile = height < 700;

  const { t } = useTranslation();
  useEffect(() => {
    if (location.state) {
      try {
        viewModel.init(location.state);
      } catch (error) {
        alert(error);
      }
    }
  }, []);

  const gameData = viewModel.gameData;

  const [anchorElPosition, setAnchorElPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    window.history.pushState(null, "", location.pathname);

    const handlePopState = () => {
      window.history.pushState(null, "", location.pathname);
      navigate(ROUTE_ENDPOINTS.GAME, { state: location.state, replace: true });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate, location.pathname]);

  const [showExitAlertDialog, setShowExitAlertDialog] = useState(false);
  const [showSaveGameAlertDialog, setShowSaveGameAlertDialog] = useState(false);

  useEffect(() => {
    if (viewModel.question && viewModel.answer) {
      setAnchorElPosition({
        top: 0,
        left: 0,
      });
    }
  }, [viewModel.question, viewModel.answer]);

  return (
    <HorizontalOnlyContainer>
      {gameData ? (
        <Box>
          <Stack
            width={"100vw"}
            height={"100vh"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Stack
              width={"100%"}
              bgcolor={"rgba(135,40,77,1)"}
              p={3}
              height={"8vh"}
            >
              <TopBarView
                gameData={gameData}
                addOrDeductPointsFromATeam={
                  viewModel.addOrDeductPointsFromATeam
                }
                onLifelineClicked={viewModel.onLifelineClicked}
                onExitButtonClicked={() => setShowExitAlertDialog(true)}
              />
            </Stack>
            <Stack
              width={"100vw"}
              height={"100%"}
              alignItems={"center"}
              justifyContent={"center"}
              sx={{ p: 1 }}
            >
              <Grid2
                container
                rowSpacing={isMobile ? 1 : 4}
                columnSpacing={isMobile ? 1 : 2}
              >
                {gameData?.categories.map((category) => {
                  return (
                    <Grid2 key={category.id} size={4}>
                      <CategoryItemView
                        category={category}
                        canAnyTeamChooseAnyQuestion={
                          gameData.canAnyTeamChooseAnyQuestion
                        }
                        onDifficultyClicked={(_, d, index) => {
                          viewModel.getRandomQuestion(category, d, index);
                        }}
                        teamTurn={gameData.currentTeamTurn}
                      />
                    </Grid2>
                  );
                })}
              </Grid2>
            </Stack>
            <Stack
              width={"100%"}
              bgcolor={COLOR_MAIN}
              pt={2}
              pb={2}
              height={"23vh"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Stack
                width={"100%"}
                height={"100%"}
                alignItems={"center"}
                justifyContent={"space-between"}
                direction={"row"}
                sx={{ direction: "ltr" }}
              >
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  width={"100%"}
                  justifyContent={"center"}
                >
                  <CustomImageView
                    width={isMobile ? 80 : 160}
                    height={isMobile ? 150 : 360}
                    src={require("../../../assets/images/rodeo_sponsor_image.png")}
                  />
                  <CustomImageView
                    width={isMobile ? 80 : 160}
                    height={isMobile ? 80 : 250}
                    scale="contain"
                    src={require("../../../assets/images/gswf_sponsor_image.png")}
                    props={{ pt: isMobile ? 2 : 3.5 }}
                  />
                </Stack>
                <CustomImageView
                  width={"100%"}
                  height={isMobile ? 40 : 110}
                  scale="contain"
                  src={require("../../../assets/images/platina_logo_sponsor.png")}
                />
                {/* <CustomImageView
                  width={"100%"}
                  height={isMobile ? 80 : 120}
                  scale="contain"
                  src={require("../../../assets/images/q_wave_image2.png")}
                /> */}
              </Stack>
            </Stack>
          </Stack>

          <CustomPopoverView
            onClose={() => {}}
            anchorElPosition={anchorElPosition}
            transitionDuration={500}
            layout={
              <QuestionView
                onClosePopover={({ who, hasGivenRate }) => {
                  setAnchorElPosition(null);
                  viewModel.OnQuestionAnswered({ who, hasGivenRate });
                }}
                currentGameId={gameData.resultGame?._id}
                currentQuestionIndex={viewModel.currentQuestionIndexNumber}
                gameDataInput={gameData}
                questionInput={viewModel.question}
                answerInput={viewModel.answer}
                hasGivenRate={viewModel.hasGivenRate}
              />
            }
          />

          <CustomAlertDialog
            open={showExitAlertDialog}
            title={t("exit_game")}
            description={t("are_you_sure_you_want_exit_game")}
            handlePositiveButtonClick={() => {
              setShowExitAlertDialog(false);
              setShowSaveGameAlertDialog(true);
            }}
            handleNegativeButtonClick={() => {
              setShowExitAlertDialog(false);
            }}
          />

          <CustomAlertDialog
            open={showSaveGameAlertDialog}
            title={t("save_the_game")}
            description={t("do_you_want_to_save_this_game")}
            handlePositiveButtonClick={() => {
              setShowSaveGameAlertDialog(false);
              viewModel.saveGame();
            }}
            handleNegativeButtonClick={() => {
              setShowSaveGameAlertDialog(false);
              navigate(ROUTE_ENDPOINTS.HOME_PLAY, { replace: true });
            }}
          />
        </Box>
      ) : (
        <Stack
          width={"100vw"}
          height={"100vh"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          GAME WAS EXITED UNEXPECTEDLY
        </Stack>
      )}
    </HorizontalOnlyContainer>
  );
}

function TopBarView({
  gameData,
  addOrDeductPointsFromATeam,
  onLifelineClicked,
  onExitButtonClicked,
}: {
  gameData: GameData;
  addOrDeductPointsFromATeam: (isFirstTeam: boolean, add: boolean) => void;
  onLifelineClicked: (lifelineId: string, isFirstTeam: boolean) => void;
  onExitButtonClicked: () => void;
}) {
  const { height } = useWindowDimensions();
  const isMobile = height < 700;

  return (
    <Stack height={"100%"} position={"relative"}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        width={"100%"}
        justifyContent={"space-between"}
        height={"100%"}
      >
        <Stack direction={"row"} alignItems={"center"} gap={2}>
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <CustomTextView
              text={gameData.firstTeamName}
              textColor={
                gameData.currentTeamTurn == TeamTurns.teamA
                  ? COLOR_GOLD
                  : "lightGray"
              }
              variant={isMobile ? "body2" : "h5"}
              fontWeight={
                gameData.currentTeamTurn == TeamTurns.teamA ? "bold" : "normal"
              }
            />
            <CustomTextView
              text={gameData.firstTeamScore.toString()}
              textColor={COLOR_WHITE}
              variant={isMobile ? "h6" : "h4"}
              fontWeight={"bold"}
            />
          </Stack>
          <LifelinesView
            lifelines={gameData.firstTeamLifelines}
            onLifelineClicked={(l) => {
              onLifelineClicked(l, true);
            }}
            size={"3vw"}
            isMidQuestion={false}
            defaultColor={COLOR_MAIN}
            selectedColor={"rgba(216,5,49,0.9)"}
            disabledColor={COLOR_LIGHT_GRAY}
          />
          <Stack direction={"row"} alignItems={"center"} gap={0.5}>
            <PlusMinusButtonView
              onClicked={() => {
                addOrDeductPointsFromATeam(true, false);
              }}
              isPlus={false}
            />
            <PlusMinusButtonView
              onClicked={() => {
                addOrDeductPointsFromATeam(true, true);
              }}
              isPlus
            />
          </Stack>
        </Stack>

        <Stack direction={"row"} alignItems={"center"} gap={2}>
          <Stack direction={"row"} alignItems={"center"} gap={0.5}>
            <PlusMinusButtonView
              onClicked={() => {
                addOrDeductPointsFromATeam(false, true);
              }}
              isPlus
            />
            <PlusMinusButtonView
              onClicked={() => {
                addOrDeductPointsFromATeam(false, false);
              }}
              isPlus={false}
            />
          </Stack>
          <LifelinesView
            lifelines={gameData.secondTeamLifelines}
            onLifelineClicked={(l) => {
              onLifelineClicked(l, false);
            }}
            size={"3vw"}
            isMidQuestion={false}
            defaultColor={COLOR_MAIN}
            selectedColor={"rgba(216,5,49,0.9)"}
            disabledColor={COLOR_LIGHT_GRAY}
          />
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <CustomTextView
              text={gameData.secondTeamScore.toString()}
              textColor={COLOR_WHITE}
              variant={isMobile ? "h6" : "h4"}
              fontWeight={"bold"}
            />
            <CustomTextView
              text={gameData.secondTeamName}
              textColor={
                gameData.currentTeamTurn == TeamTurns.teamB
                  ? COLOR_GOLD
                  : "lightGray"
              }
              variant={isMobile ? "body2" : "h5"}
              fontWeight={
                gameData.currentTeamTurn == TeamTurns.teamB ? "bold" : "normal"
              }
            />
          </Stack>
        </Stack>
      </Stack>

      <Stack
        direction={"row"}
        alignItems={"center"}
        gap={1}
        justifyContent={"center"}
        position={"absolute"}
        alignSelf={"center"}
        height={"100%"}
      >
        <IconButton
          sx={{
            background: "red",
            borderRadius: 50,
            width: "3vw",
            height: "3vw",
            "&:hover": {
              backgroundColor: COLOR_MAIN,

              "@media (hover: none)": {
                backgroundColor: "red",
              },
            },
          }}
          onClick={onExitButtonClicked}
        >
          <ExitToAppRounded
            sx={{ color: COLOR_WHITE, width: "2vw", height: "2vw" }}
          />
        </IconButton>
        <Stack width={"8vw"}>
          <CustomImageView
            src={require("../../../assets/images/white_writing_logo.png")}
            width={"100%"}
            height={"2vw"}
            scale="contain"
          />
        </Stack>
      </Stack>
    </Stack>
  );
}

function PlusMinusButtonView({
  isPlus,
  onClicked,
}: {
  isPlus: boolean;
  onClicked: () => void;
}) {
  return (
    <IconButton
      onClick={onClicked}
      sx={{
        background: COLOR_BLACK,
        borderRadius: 50,
        width: "2.5vw",
        height: "2.5vw",
        "&:hover": {
          backgroundColor: COLOR_MAIN,
          "@media (hover: none)": {
            backgroundColor: COLOR_BLACK,
          },
        },
      }}
    >
      <CustomImageView
        src={
          isPlus
            ? require("../../../assets/images/plus_icon.png")
            : require("../../../assets/images/minus_icon.png")
        }
        width={"1.5vw"}
        height={"1.5vw"}
      />
    </IconButton>
  );
}

function CategoryItemView({
  category,
  teamTurn,
  onDifficultyClicked,
  canAnyTeamChooseAnyQuestion,
}: {
  category: Category;
  teamTurn: TeamTurns;
  onDifficultyClicked: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    difficulty: Difficulty,
    index: number
  ) => void;
  canAnyTeamChooseAnyQuestion: boolean;
}) {
  const currentTurn = teamTurn;
  const { height } = useWindowDimensions();
  const { t } = useTranslation();
  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      width={"100%"}
      height={"100%"}
      justifyContent={"center"}
      spacing={0}
    >
      <Stack alignItems={"center"} spacing={height < 700 ? "1.2vh" : "1.2vmax"}>
        <CategoryDifficultyButtonView
          title={getDifficultyInfo(Difficulty.EASY, t).point.toString()}
          isForFirstTeam={true}
          disabled={
            !canAnyTeamChooseAnyQuestion && currentTurn == TeamTurns.teamB
          }
          onClicked={(e) => onDifficultyClicked(e, Difficulty.EASY, 0)}
          visible={
            category.pointsHashmap.get(TeamTurns.teamA)?.get(Difficulty.EASY) ==
            false
          }
        />
        <CategoryDifficultyButtonView
          title={getDifficultyInfo(Difficulty.MEDIUM, t).point.toString()}
          isForFirstTeam={true}
          disabled={
            !canAnyTeamChooseAnyQuestion && currentTurn == TeamTurns.teamB
          }
          onClicked={(e) => onDifficultyClicked(e, Difficulty.MEDIUM, 1)}
          visible={
            category.pointsHashmap
              .get(TeamTurns.teamA)
              ?.get(Difficulty.MEDIUM) == false
          }
        />
        <CategoryDifficultyButtonView
          title={getDifficultyInfo(Difficulty.HARD, t).point.toString()}
          isForFirstTeam={true}
          disabled={
            !canAnyTeamChooseAnyQuestion && currentTurn == TeamTurns.teamB
          }
          onClicked={(e) => onDifficultyClicked(e, Difficulty.HARD, 2)}
          visible={
            category.pointsHashmap.get(TeamTurns.teamA)?.get(Difficulty.HARD) ==
            false
          }
        />
      </Stack>

      <Stack
        width={"20vw"}
        alignItems={"center"}
        sx={{
          border: "solid 2px " + COLOR_MAIN,
          borderRadius: 4,
          boxShadow: 20,
          position: "relative",
        }}
      >
        <CustomImageView
          src={category.imageUrl}
          width={"15vw"}
          height={"15vw"}
          props={{
            borderRadius: 3.5,
          }}
        />
        <Stack
          sx={{
            position: "absolute",
            bottom: 0,
            zIndex: 2,
            background: "rgba(135,40,77,0.8)",
            width: "100%",
            alignItems: "center",
            borderRadius: 3.5,
            p: 0.2,
          }}
        >
          <CustomTextView
            text={category.title}
            textColor={COLOR_WHITE}
            fontWeight="bold"
            variant={height < 700 ? "body1" : "h5"}
          />
        </Stack>
      </Stack>
      <Stack alignItems={"center"} spacing={height < 700 ? "1.2vh" : "1.2vmax"}>
        <CategoryDifficultyButtonView
          title={getDifficultyInfo(Difficulty.EASY, t).point.toString()}
          isForFirstTeam={false}
          disabled={
            !canAnyTeamChooseAnyQuestion && currentTurn == TeamTurns.teamA
          }
          onClicked={(e) => onDifficultyClicked(e, Difficulty.EASY, 3)}
          visible={
            category.pointsHashmap.get(TeamTurns.teamB)?.get(Difficulty.EASY) ==
            false
          }
        />

        <CategoryDifficultyButtonView
          title={getDifficultyInfo(Difficulty.MEDIUM, t).point.toString()}
          isForFirstTeam={false}
          disabled={
            !canAnyTeamChooseAnyQuestion && currentTurn == TeamTurns.teamA
          }
          onClicked={(e) => onDifficultyClicked(e, Difficulty.MEDIUM, 4)}
          visible={
            category.pointsHashmap
              .get(TeamTurns.teamB)
              ?.get(Difficulty.MEDIUM) == false
          }
        />
        <CategoryDifficultyButtonView
          title={getDifficultyInfo(Difficulty.HARD, t).point.toString()}
          isForFirstTeam={false}
          disabled={
            !canAnyTeamChooseAnyQuestion && currentTurn == TeamTurns.teamA
          }
          onClicked={(e) => onDifficultyClicked(e, Difficulty.HARD, 5)}
          visible={
            category.pointsHashmap.get(TeamTurns.teamB)?.get(Difficulty.HARD) ==
            false
          }
        />
      </Stack>
    </Stack>
  );
}

function CategoryDifficultyButtonView({
  title,
  disabled,
  isForFirstTeam,
  visible,
  onClicked,
}: {
  title: string;
  disabled: boolean;
  isForFirstTeam: boolean;
  visible: boolean;
  onClicked: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) {
  const { height } = useWindowDimensions();
  const isMobile = height < 700;
  const borderRadius = 50;
  const { i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const topAndBottomLeftBorderRadius = isForFirstTeam
    ? isRtl
      ? 0
      : borderRadius
    : isRtl
    ? borderRadius
    : 0;

  const topAndBottomRightBorderRadius = isForFirstTeam
    ? isRtl
      ? borderRadius
      : 0
    : isRtl
    ? 0
    : borderRadius;
  return (
    <Button
      disabled={disabled}
      onClick={onClicked}
      variant="contained"
      sx={{
        borderTopLeftRadius: topAndBottomLeftBorderRadius,
        borderBottomLeftRadius: topAndBottomLeftBorderRadius,
        borderTopRightRadius: topAndBottomRightBorderRadius,
        borderBottomRightRadius: topAndBottomRightBorderRadius,
        background: COLOR_MAIN,
        "&:hover": {
          backgroundColor: "rgba(135,40,77,0.8)",
          "@media (hover: none)": {
            backgroundColor: COLOR_MAIN,
          },
        },
        p: height < 700 ? 0.4 : "0.8vmax",
        visibility: visible ? undefined : "hidden",
      }}
      style={
        isMobile
          ? {
              minWidth: "7vw",
              maxWidth: "7vw",
            }
          : undefined
      }
    >
      <CustomTextView
        text={title}
        variant={height < 700 ? undefined : "h5"}
        fontSize={14}
        textColor={COLOR_WHITE}
        fontWeight="bold"
      />
    </Button>
  );
}
